/**
 * Centralized API Client
 *
 * Provides a unified interface for all API calls with:
 * - Automatic token injection
 * - Global error handling
 * - Request/response interceptors
 * - Automatic retry logic
 */

import { getAuthToken, clearAuthSession } from '../utils/auth';
import { getB2BAuthToken, clearB2BAuthSession } from '../utils/b2bAuth';
import { API_BASE_URL } from '../config/api';

/**
 * Custom API Error class with status code and response data
 */
export class APIError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Normalize a FastAPI/Pydantic error response to a single human-readable string.
 *
 * Handles all three response shapes returned by the backend:
 *   - 422 Unprocessable Entity: { detail: [{ loc, msg, type, input, ... }, ...] }
 *   - 4xx/5xx with HTTPException: { detail: "human-readable string" }
 *   - Plain text body or unknown shape
 *
 * Without this, callers do `data?.detail || data?.message || data` and get a
 * non-string (the array) for 422 — which then drops to a generic toast.
 */
export function extractErrorMessage(data) {
  if (data == null) return 'Request failed';
  if (typeof data === 'string') return data;
  // Pydantic 422 — detail is an array of error objects.
  if (Array.isArray(data.detail)) {
    const parts = data.detail
      .map((e) => {
        if (!e || typeof e !== 'object') return '';
        // Drop the leading "body"/"path"/"query" segment from `loc` for brevity.
        const loc = Array.isArray(e.loc) ? e.loc.slice(1).join('.') : '';
        const msg = typeof e.msg === 'string' ? e.msg : '';
        if (!msg) return '';
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean);
    return parts.length ? parts.join('; ') : 'Validation error';
  }
  if (typeof data.detail === 'string') return data.detail;
  if (typeof data.message === 'string') return data.message;
  return 'Request failed';
}

/**
 * Make an API call with automatic error handling and token injection
 *
 * @param {string} endpoint - API endpoint (e.g., '/api/auth/login')
 * @param {object} options - Fetch options (method, body, headers, etc.)
 * @returns {Promise<any>} - Parsed JSON response
 * @throws {APIError} - On HTTP errors or network failures
 */
export async function apiCall(endpoint, options = {}) {
  // Pick the right token: B2B endpoints carry the B2B JWT (claim type:"b2b"),
  // everything else carries the C2C JWT. Tokens live under different localStorage
  // keys so a user can be signed in to both products at once.
  const isB2BCall = endpoint.startsWith('/api/b2b/');
  const token = isB2BCall ? getB2BAuthToken() : getAuthToken();

  // Build request configuration
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  };

  const url = `${API_BASE_URL}${endpoint}`;

  try {
    const response = await fetch(url, config);

    // Handle 401/403 globally — clear *only* the relevant session (C2C or B2B)
    // and redirect to the matching auth screen. The two products live on the
    // same domain but keep their own auth state. We leave a sessionStorage
    // flag so the destination screen can explain why the user was bounced
    // (e.g. "your session expired mid-question-generation, please log in
    // again").
    if (response.status === 401 || response.status === 403) {
      const path = window.location.pathname;

      if (isB2BCall) {
        clearB2BAuthSession();
        // Don't bounce if we're already on a B2B auth flow.
        if (!path.startsWith('/b2b/auth')) {
          try {
            sessionStorage.setItem('auth-bounce-reason', 'b2b-session-expired');
          } catch { /* sessionStorage may be unavailable in private mode */ }
          window.location.href = '/b2b/auth';
        }
      } else {
        clearAuthSession();
        // Stay put on C2C auth pages and on any /b2b/* page (B2B handles its own).
        if (!path.startsWith('/login') &&
            !path.startsWith('/register') &&
            !path.startsWith('/b2b/')) {
          try {
            sessionStorage.setItem('auth-bounce-reason', 'c2c-session-expired');
          } catch { /* sessionStorage may be unavailable in private mode */ }
          window.location.href = '/';
        }
      }

      throw new APIError('Unauthorized - please login again', response.status);
    }

    // Try to parse JSON response
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    // Check if request was successful
    if (!response.ok) {
      throw new APIError(extractErrorMessage(data), response.status, data);
    }

    return data;
  } catch (error) {
    // Re-throw APIError as-is
    if (error instanceof APIError) {
      throw error;
    }

    // Wrap network errors
    throw new APIError(
      'Network error - please check your connection',
      0,
      { originalError: error.message }
    );
  }
}

/**
 * Convenience methods for common HTTP verbs
 */
export const api = {
  /**
   * GET request
   * @param {string} endpoint
   * @returns {Promise<any>}
   */
  get: (endpoint) => apiCall(endpoint, { method: 'GET' }),

  /**
   * POST request with JSON body
   * @param {string} endpoint
   * @param {object} data
   * @returns {Promise<any>}
   */
  post: (endpoint, data) => apiCall(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  /**
   * PUT request with JSON body
   * @param {string} endpoint
   * @param {object} data
   * @returns {Promise<any>}
   */
  put: (endpoint, data) => apiCall(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  /**
   * DELETE request
   * @param {string} endpoint
   * @returns {Promise<any>}
   */
  delete: (endpoint) => apiCall(endpoint, { method: 'DELETE' }),

  /**
   * PATCH request with JSON body
   * @param {string} endpoint
   * @param {object} data
   * @returns {Promise<any>}
   */
  patch: (endpoint, data) => apiCall(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
};

/**
 * Helper for uploading files (multipart/form-data)
 * @param {string} endpoint
 * @param {FormData} formData
 * @returns {Promise<any>}
 */
export async function uploadFile(endpoint, formData) {
  const token = getAuthToken();

  return apiCall(endpoint, {
    method: 'POST',
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      // Don't set Content-Type - browser will set it with boundary
    },
    body: formData,
  });
}

export default api;
